import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import * as api from '../../api/app/opcClientApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import { useTranslation } from '../../contexts/translation';
import { OpcClient, OpcClientInputs } from '../../types/app/opcClient.types';
import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/opcClient.functions';
import { paths } from '../../utils/core/routes';
import { OpcClientForm } from './index';
import { useStyles } from './OpcClientForm.styles';

export function OpcClientDetail() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [defaultValues, setDefaultValues] = useState<OpcClientInputs | null>(null);
  const { mutate: mutateUpdateOpcClient } = useMutation(api.updateOpcClient);
  const { data, isLoading, isError } = useQuery<OpcClient>(['opcClient', id], () =>
    api.getOpcClient(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      instanceName: data.instanceName,
      endpointAddress: data.endpointAddress,
      comment: data.comment,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<OpcClientInputs> = (data: OpcClientInputs) => {
    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      id: Number(id!),
    });

    mutateUpdateOpcClient(
      { ...requestData },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.opc-client.item-edited'), { variant: 'success' });
          navigate(paths.OPCCLIENT_LIST);
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <PageHeader title={t('page.opc-client.headline-edit')} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={4}>
            <Paper className={classes.paper}>
              <OpcClientForm
                onSubmit={handleOnSubmit}
                onBack={() => navigate(paths.OPCCLIENT_LIST)}
                defaultValues={defaultValues}
              />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
